import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { exportTicketEvents } from '../services/apiService';

const Report = () => {
  const [isExportingProduction, setIsExportingProduction] = useState(false);
  const [isExportingTest, setIsExportingTest] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Add "report-page" class to body when component mounts
  useEffect(() => {
    document.body.classList.add('report-page');

    // Remove class when component unmounts
    return () => {
      document.body.classList.remove('report-page');
    };
  }, []);

  const handleExportProduction = async () => {
    setIsExportingProduction(true);
    setError(null);
    setSuccess(null);

    try {
      await exportTicketEvents('production');
      setSuccess('Production ticket events exported successfully!');
    } catch (err) {
      console.error('Export production error:', err);
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      setError({ message: errorMessage, environment: 'production' });
    } finally {
      setIsExportingProduction(false);
    }
  };

  const handleExportTest = async () => {
    setIsExportingTest(true);
    setError(null);
    setSuccess(null);

    try {
      await exportTicketEvents('test');
      setSuccess('Test ticket events exported successfully!');
    } catch (err) {
      console.error('Export test error:', err);
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      setError({ message: errorMessage, environment: 'test' });
    } finally {
      setIsExportingTest(false);
    }
  };

  const handleCopyError = () => {
    if (error && error.message) {
      navigator.clipboard.writeText(error.message).then(() => {
        // Successfully copied to clipboard
      }).catch(err => {
        console.error('Failed to copy error:', err);
        // Fallback: select the textarea content
        const textarea = document.querySelector('.report-error-box');
        if (textarea) {
          textarea.select();
          textarea.setSelectionRange(0, 99999); // For mobile devices
        }
      });
    }
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Data Export Tool</h1>
        <p>for <img src="/images/andonx-logo-mini.png" alt="AndonX Logo" /></p>
      </div>

      <div className="report-content">
        <div className="report-description">
          <ol>
            <li className="report-desc-item">Note which PLEX environment you are working in</li>
            <li className="report-desc-item">Export ticket event data as a CSV file</li>
          </ol>
        </div>

        <div className="export-columns">
          <div className="export-column export-column-test">
            <h2 className="export-column-header">TEST<br/>Environment</h2>
            <p className="export-column-description">
            Access PLEX Test Data
            </p>
            <button
              className="export-button export-test"
              onClick={handleExportTest}
              disabled={isExportingProduction || isExportingTest}
            >
              {isExportingTest ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i> Download Test Events
                </>
              )}
            </button>
          </div>

          <div className="export-column export-column-production">
            <h2 className="export-column-header">PRODUCTION<br/>Environment</h2>
            <p className="export-column-description">
            Access PLEX live data
            </p>
            <button
              className="export-button export-production"
              onClick={handleExportProduction}
              disabled={isExportingProduction || isExportingTest}
            >
              {isExportingProduction ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i> Download Production Events
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="report-message report-error">
            <p className="report-error-title">
              {error.environment === 'production'
                ? 'Production ticket event export was unsuccessful.'
                : 'Test ticket event export was unsuccessful.'}
            </p>

            <div className="report-error-box-container">
              <textarea
                className="report-error-box"
                value={error.message}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button
                className="report-error-copy-button"
                onClick={handleCopyError}
                title="Copy error message"
              >
                <i className="fas fa-copy"></i> Copy
              </button>
            </div>

            <p className="report-error-instructions">
              Copy the error message above and include in an email to{' '}
              <a href="mailto:andonx-errors@strandproducts.com" className="report-error-email">
                andonx-errors@strandproducts.com
              </a>.
            </p>
          </div>
        )}

        {success && (
          <div className="report-message report-success">
            <i className="fas fa-check-circle"></i> {success}
          </div>
        )}

        <div className="back-link-container">
          <Link to="/" className="back-link">
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Report;

